/**
 * ZEN ERP — Router Shell
 * Tüm sekme tabanlı sayfa routing'i burada. App.js yalnızca
 * AppShell (auth, tema, context sağlayıcılar) içerir.
 *
 * Yeni sekme eklemek: sadece bu dosyaya yeni import + {tab === "x"} satırı yeter.
 */
import React, { useState, useContext, useEffect } from "react";
import { toast } from "sonner";
import { NavigationCtx, UICtx } from "./appContexts";
import api from "./api";

// ── Sayfa Modülleri ───────────────────────────────────────────────────────────
import { Dashboard }         from "./pages/DashboardPage";
import { OrdersModule }      from "./pages/OrdersPage";
import { QuotesListModule }  from "./pages/QuotesListModule";
import CustomersModule       from "./pages/CustomersPage";
import AccountingModule      from "./pages/AccountingPage";
import {
  PackingListModule, StockManagementModule, PriceListsModule,
  InvoicesModule,
}                            from "./pages/CatalogPage";
import LabelPrintPage        from "./pages/LabelPrintPage";
import ReportsModule         from "./pages/ReportsPage";
import KartelaModule         from "./pages/KartelaPage";
import SettingsModule        from "./pages/SettingsPage";
import PurchaseOrdersModule  from "./pages/PurchaseOrdersPage";
import ArchivePage           from "./pages/ArchivePage";
import AppointmentsPage      from "./pages/AppointmentsPage";
import WorkshopsPage         from "./pages/WorkshopsPage";
import WhatsAppInboxPage     from "./pages/WhatsAppInboxPage";
import BayilojiHistoryPage   from "./pages/BayilojiHistoryPage";
import CRMModule             from "./crm";
import AnimasyonDemoPage      from "./pages/AnimasyonDemoPage";

// ── CRM PDF callback için minimal imports ────────────────────────────────────
import { buildTeklifPrintHTML } from "./quotes";

export function RouterShell() {
  const { activeTab: tab, setTab, subTab } = useContext(NavigationCtx);
  const { tr } = useContext(UICtx);
  const [editingOrder, setEditingOrder] = useState(null);

  // Derin bağlantı: localhost/#animasyon-demo → demo sayfası doğrudan açılır
  // (hem ilk yüklemede hem adres değişince tetiklenir)
  useEffect(() => {
    const apply = () => {
      if (window.location.hash === "#animasyon-demo") setTab("animasyon_demo");
    };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, [setTab]);

  // ── CRM → Teklif PDF üret (CRMModule'e callback olarak geçirilir) ──────────
  const handleCRMGenerateQuotePDF = async (quoteId) => {
    try {
      const [r, rateRes, customerRes, bankRes] = await Promise.all([
        api.get("/quotes"),
        api.get("/rates"),
        api.get("/customers"),
        api.get("/bank-accounts"),
      ]);
      const q = r.data.find((x) => x.id === quoteId);
      if (!q) { toast.error("Teklif bulunamadı"); return null; }
      const customerInfo = (customerRes.data || []).find((c) => c.id === q.customerId) || null;
      const bankAccount  = (bankRes.data  || []).find((b) => b.id === q.bankAccountId)
                        || (bankRes.data  || []).find((b) => b.isDefault) || null;
      const fullQuote = { ...q, customerInfo, customerPhone: customerInfo?.phone, bankAccount, rates: rateRes.data };
      const html         = buildTeklifPrintHTML(fullQuote, "tr");
      const barcodeValue = `${String(q.brand || "zen").toUpperCase()}-${q.quoteNo || "X"}-R${String(q.revisionNo || 1).padStart(2, "0")}`;
      const { data }     = await api.post("/share/html-pdf", {
        html,
        filename: `Teklif-${q.quoteNo || q.id}.pdf`,
        refType: "quote",
        refId: q.id,
        barcodeValue,
      });
      return `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
    } catch (e) {
      toast.error("PDF oluşturulamadı");
      return null;
    }
  };

  // ── CRM → Sipariş formu aç ────────────────────────────────────────────────
  const handleCRMOpenOrderForm = (lead) => {
    setEditingOrder({
      _new:       true,
      _leadId:    lead.id,
      _leadTitle: lead.title || lead.customerName || "",
      customerId: lead.customerId || "",
      notes:      lead.title || "",
    });
    setTab("orders");
  };

  return (
    <main className="flex-1 min-w-0 w-full p-3 sm:p-6 overflow-y-auto overflow-x-hidden bg-slate-50 dark:bg-[#0C1220]">
      {tab === "dashboard"          && <Dashboard setTab={setTab} setEditingOrder={setEditingOrder} />}
      {tab === "orders"             && <OrdersModule editingOrder={editingOrder} setEditingOrder={setEditingOrder} />}
      {tab === "quotes"             && <QuotesListModule onNavigate={setTab} />}
      {tab === "customers"          && <CustomersModule subTab={subTab} />}
      {tab === "crm"                && (
        <CRMModule
          lang="tr"
          tr={tr}
          onGenerateQuotePDF={handleCRMGenerateQuotePDF}
          onOpenOrderForm={handleCRMOpenOrderForm}
        />
      )}
      {tab === "invoices"           && <InvoicesModule subTab={subTab} />}
      {tab === "checks"             && <PackingListModule />}
      {tab === "catalog_hierarchy"  && <KartelaModule />}
      {tab === "stock"              && <StockManagementModule />}
      {tab === "pricelists"         && <PriceListsModule />}
      {tab === "purchase_orders"    && <PurchaseOrdersModule />}
      {tab === "archive"            && <ArchivePage />}
      {tab === "appointments"       && <AppointmentsPage />}
      {tab === "workshops"          && <WorkshopsPage />}
      {tab === "whatsapp_inbox"     && <WhatsAppInboxPage />}
      {tab === "bayiloji_history"   && <BayilojiHistoryPage />}
      {tab === "animasyon_demo"     && <AnimasyonDemoPage />}
      {tab === "label_print"        && <LabelPrintPage />}
      {tab === "accounting"         && <AccountingModule />}
      {tab === "reports"            && <ReportsModule />}
      {tab === "settings"           && <SettingsModule />}
    </main>
  );
}
